import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./RequiredLessonMaterials.module.scss";
import Modal from "src/components/modal/Modal";
import Button from "src/components/button/Button";
import { LordIcon } from "@src/components/lord-icon/lord-icon";
import { IconInfo, IconProjRequired } from "@src/constants";
import { SllLesson, SllModule } from "@src/types";
import MaterialCard from "@src/components/materialCard/SllCard";
import { hasArrayValues } from "@src/utils/helpers";
import CheatSheetSection from "./CheatSheetSection";
import { shouldShowLessonMaterialsCheatSheet } from "./lessonMaterialsCheatSheet";
import LessonCompleteSnackbar from "@src/components/lesson-complete-snackbar/LessonCompleteSnackbar";

const AUTO_COMPLETE_COUNTDOWN_MS = 5000;

type Props = {
  open: boolean;
  lesson?: SllLesson;
  onClose: () => void;
  onStartLesson: () => void;
  onCompleteLesson: () => void | Promise<void>;
  onUndoCompleteLesson?: () => void | Promise<void>;
  openFeedback?: (e: any) => void;
  completedPercent?: number;
  startLabel?: string;
  hasLessonPresentationContents?: boolean;
  showLessonMaterialsNotice?: boolean;
  isEducatorCaregiverLessonLayout?: boolean | null;
  /** Lets parent (lesson modal X) flush pending complete before unmounting. */
  bindCloseHandler?: (handler: (() => Promise<void>) | null) => void;
  handleDownloadMaterial?: (
    content: SllModule,
    lesson?: SllLesson,
    needPrint?: boolean,
  ) => void;
};

function RequiredLessonMaterials(props: Props) {
  const {
    open,
    lesson,
    startLabel,
    showLessonMaterialsNotice = false,
    hasLessonPresentationContents = false,
    completedPercent,
    onClose,
    onStartLesson,
    onCompleteLesson,
    handleDownloadMaterial,
    isEducatorCaregiverLessonLayout,
    bindCloseHandler,
  } = props;

  const isMaterialOnly = !hasLessonPresentationContents;
  const isAlreadyComplete = completedPercent === 100;

  const [countdownMs, setCountdownMs] = useState(AUTO_COMPLETE_COUNTDOWN_MS);
  const [countdownActive, setCountdownActive] = useState(false);
  /** UI-only: countdown finished or user marked complete; API runs on Close. */
  const [pendingComplete, setPendingComplete] = useState(false);
  const [completing, setCompleting] = useState(false);
  const autoCompletedRef = useRef(false);
  /** After Undo in this modal session, do not restart the 5s timer until re-open. */
  const skippedCountdownAfterUndoRef = useRef(false);

  const requiredMaterials = useMemo(() => {
    if (!lesson?.materials) return [];
    return lesson?.materials.filter(
      (material) => material?.isRequired === true,
    );
  }, [lesson?.materials]);

  const optionalMaterials = useMemo(() => {
    if (!lesson?.materials) return [];
    return lesson?.materials.filter(
      (material) => material?.isRequired === false,
    );
  }, [lesson?.materials]);

  const handleDownloadLessonMaterial = useCallback(
    (content: SllModule) => {
      handleDownloadMaterial?.(content, lesson);
    },
    [lesson, handleDownloadMaterial],
  );

  const handlePrintMaterial = useCallback(
    (content: SllModule) => {
      handleDownloadMaterial?.(content, lesson, true);
    },
    [lesson, handleDownloadMaterial],
  );

  const showCheatSheet = shouldShowLessonMaterialsCheatSheet(
    isEducatorCaregiverLessonLayout ?? lesson?.isEducatorCaregiverLessonLayout,
    hasArrayValues(requiredMaterials),
  );

  // Reset session state whenever the modal closes; start countdown on fresh open.
  useEffect(() => {
    if (!open) {
      setCountdownActive(false);
      setCountdownMs(AUTO_COMPLETE_COUNTDOWN_MS);
      setPendingComplete(false);
      autoCompletedRef.current = false;
      skippedCountdownAfterUndoRef.current = false;
      return;
    }

    if (
      !isMaterialOnly ||
      isAlreadyComplete ||
      skippedCountdownAfterUndoRef.current
    ) {
      setCountdownActive(false);
      return;
    }

    setCountdownMs(AUTO_COMPLETE_COUNTDOWN_MS);
    setCountdownActive(true);
    setPendingComplete(false);
    autoCompletedRef.current = false;
    // Only when the modal opens (or lesson changes) — not when completion status flips
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isMaterialOnly, lesson?.id]);

  // Tick countdown
  useEffect(() => {
    if (!open || !countdownActive) return;

    const startedAt = Date.now();
    const remainingAtStart = countdownMs;
    const intervalId = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const next = Math.max(0, remainingAtStart - elapsed);
      setCountdownMs(next);
      if (next <= 0) {
        window.clearInterval(intervalId);
      }
    }, 50);

    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, countdownActive]);

  // Countdown finished → pending complete UI only (persist on Close)
  useEffect(() => {
    if (!countdownActive || countdownMs > 0 || autoCompletedRef.current) return;

    autoCompletedRef.current = true;
    setCountdownActive(false);
    setPendingComplete(true);
  }, [countdownActive, countdownMs]);

  const handleClose = useCallback(async () => {
    setCountdownActive(false);

    // Commit completion only when closing while in completed UI (and not already saved)
    if (isMaterialOnly && pendingComplete && !isAlreadyComplete) {
      setCompleting(true);
      try {
        await onCompleteLesson();
      } finally {
        setCompleting(false);
      }
    }

    setPendingComplete(false);
    onClose();
  }, [
    isMaterialOnly,
    pendingComplete,
    isAlreadyComplete,
    onCompleteLesson,
    onClose,
  ]);

  useEffect(() => {
    if (!bindCloseHandler) return;
    bindCloseHandler(handleClose);
    return () => bindCloseHandler(null);
  }, [bindCloseHandler, handleClose]);

  const handleManualComplete = useCallback(() => {
    if (completing || pendingComplete || isAlreadyComplete) return;
    setCountdownActive(false);
    autoCompletedRef.current = true;
    setPendingComplete(true);
  }, [completing, pendingComplete, isAlreadyComplete]);

  const handleUndo = useCallback(() => {
    // Local only — nothing was persisted yet
    setPendingComplete(false);
    skippedCountdownAfterUndoRef.current = true;
    setCountdownActive(false);
    setCountdownMs(AUTO_COMPLETE_COUNTDOWN_MS);
    autoCompletedRef.current = false;
  }, []);

  const secondsRemaining = Math.max(1, Math.ceil(countdownMs / 1000));
  const progressPercent =
    ((AUTO_COMPLETE_COUNTDOWN_MS - countdownMs) / AUTO_COMPLETE_COUNTDOWN_MS) *
    100;

  const showCompletedState = isAlreadyComplete || pendingComplete;
  const showCountdownSnackbar =
    open && isMaterialOnly && countdownActive && !showCompletedState;
  const showUndo =
    open && isMaterialOnly && pendingComplete && !isAlreadyComplete;

  const completeButtonClassName = [
    styles.completeButton,
    countdownActive && !showCompletedState ? styles.completeButtonCounting : "",
    showCompletedState ? styles.completeButtonDone : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <Modal
        global={false}
        open={open}
        onClose={handleClose}
        closeButton={true}
        className={styles.wrapper}
      >
        <div className={styles.mainView}>
          <div className={styles.header}>
            <div className={styles.title}>{"Lesson Materials"}</div>
          </div>
          <div className={styles.content}>
            <div className={styles.scrollBody}>
              {showLessonMaterialsNotice && (
                <div className={styles.materialNote}>
                  <LordIcon
                    src={IconProjRequired}
                    isHover={true}
                    colors={{
                      primary: "#AF77B8",
                      secondary: "#AF77B8",
                    }}
                    size={24}
                  />
                  <div className={styles.materialNoteText}>
                    This lesson <span>only</span> contains downloadable
                    materials.
                  </div>
                </div>
              )}
              <div className={styles.subView}>
                <div className={styles.materialsRow}>
                  {hasArrayValues(requiredMaterials) && (
                    <div className={styles.materialContents}>
                      <div className={styles.requireTitle}>
                        Required
                        <LordIcon
                          src={IconInfo}
                          isHover={true}
                          colors={{ primary: "#FFB073", secondary: "#FFB073" }}
                          size={20}
                        />
                      </div>
                      <div className={styles.unitsDiv}>
                        {requiredMaterials.map((material, index) => {
                          return (
                            <MaterialCard
                              key={index}
                              content={material as SllModule}
                              handleDownloadMaterial={
                                handleDownloadLessonMaterial
                              }
                              handlePrintMaterial={handlePrintMaterial}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {hasArrayValues(optionalMaterials) && (
                    <div className={styles.materialContents}>
                      <div className={styles.optionTitle}>
                        Optional
                        <LordIcon
                          src={IconInfo}
                          isHover={true}
                          colors={{ primary: "#6C969A", secondary: "#6C969A" }}
                          size={20}
                        />
                      </div>
                      <div className={styles.unitsDiv}>
                        {optionalMaterials.map((material, index) => {
                          return (
                            <MaterialCard
                              key={index}
                              content={material as SllModule}
                              handleDownloadMaterial={
                                handleDownloadLessonMaterial
                              }
                              handlePrintMaterial={handlePrintMaterial}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                {showCheatSheet && <CheatSheetSection />}
              </div>
            </div>
            <div className={styles.action}>
              <Button
                size="small"
                className={styles.downButton}
                onClick={handleClose}
                label={isMaterialOnly ? "Close" : "Cancel"}
                disabled={completing}
              />
              {hasLessonPresentationContents ? (
                <Button
                  size="small"
                  className={styles.startButton}
                  onClick={onStartLesson}
                  label={startLabel ? startLabel : "Begin Lesson"}
                />
              ) : showCompletedState ? (
                <button
                  type="button"
                  className={completeButtonClassName}
                  disabled
                >
                  <span className={styles.completeButtonLabel}>
                    Lesson Complete
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  className={completeButtonClassName}
                  disabled={completing}
                  onClick={handleManualComplete}
                  style={
                    countdownActive
                      ? ({
                          ["--countdown-progress" as string]: `${progressPercent}%`,
                        } as React.CSSProperties)
                      : undefined
                  }
                >
                  <span className={styles.completeButtonLabel}>
                    Mark as Complete
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal>
      {showCountdownSnackbar && (
        <LessonCompleteSnackbar
          mode="countdown"
          secondsRemaining={secondsRemaining}
        />
      )}
      {showUndo && <LessonCompleteSnackbar mode="undo" onUndo={handleUndo} />}
    </>
  );
}

export default RequiredLessonMaterials;
